import { Injectable, InjectionToken, Provider } from '@angular/core';
import { careerMocks, participationStatusMocks, socialSecurityStatusMocks } from '../../mock-data/career.mock';
import { PaginatedResponse } from '../../shared/pagination/paginated-response';
import { ApiAdmissionStatus } from '../api-types/admission-status.api';
import { ApiResponse } from '../api-types/api-response.api';
import { ApiCareer, ApiCareerCreate, ApiCareerMetadata, ApiCareerUpdate } from '../api-types/career.api';
import { ApiSocialSecurityStatus } from '../api-types/social-security-status.api';
import { Career, CareerMetadata, CreateCareer, UpdateCareer } from '../app-types/career.app';
import { ParticipationStatus } from '../app-types/participation-status.app';
import { SocialSecurityStatus } from '../app-types/social-security-status.app';
import {
	transformApiAdmissionStatusesToAppParticipationStatuses,
	transformApiCareersToAppCareers,
	transformApiCareerToAppCareer,
	transformApiSocialSecurityStatusesToAppSocialSecurityStatuses,
	transformCareerCreateToApiCareerCreate,
	transformUpdateCareerToApiCareerUpdate,
} from '../transformers/career.transform';
import { BaseRequestService } from './base-request.service';

export interface CareerApiProvider {
	getAllByClient(clientId: string | number, offset?: number, range?: number): Promise<PaginatedResponse<Career>>;
	create(clientId: string | number, data: CreateCareer): Promise<void>;
	update(clientId: number | string, data: UpdateCareer): Promise<void>;
	removeEntry(clientId: string | number, careerId: string | number): Promise<void>;
	getAdmissionStatus(offset?: number, range?: number): Promise<PaginatedResponse<ParticipationStatus>>;
	getSocialSecurityStatus(offset?: number, range?: number): Promise<PaginatedResponse<SocialSecurityStatus>>;
	getSingle(
		clientId: number | string,
		careerId: number | string,
		assignmentId?: number | string,
	): Promise<ApiResponse<Career, CareerMetadata> | undefined>;
}

@Injectable()
export class CareerApi extends BaseRequestService implements CareerApiProvider {
	public async removeEntry(clientId: string | number, careerId: string | number): Promise<void> {
		await this.remove<void>(`clients/${clientId}/careers/${careerId}`);
	}
	public async getAllByClient(
		clientId: string | number,
		offset?: number,
		range?: number,
	): Promise<PaginatedResponse<Career>> {
		const response = await this.get<PaginatedResponse<ApiCareer>>(`clients/${clientId}/careers`, { offset, range });
		const page: PaginatedResponse<Career> = {
			entities: transformApiCareersToAppCareers(response.entities),
			offset: response.offset,
			range: response.range,
			totalEntities: response.totalEntities,
		};
		return page;
	}

	public async create(clientId: string | number, data: CreateCareer): Promise<void> {
		const body = transformCareerCreateToApiCareerCreate(data, clientId);
		await this.post<void, ApiCareerCreate>(`clients/${clientId}/careers`, body);
		return;
	}

	public async update(clientId: number | string, data: UpdateCareer): Promise<void> {
		const body: ApiCareerUpdate = transformUpdateCareerToApiCareerUpdate(data);
		await this.put<void, ApiCareerUpdate>(`clients/${clientId}/careers/${data.id}`, body);
		return;
	}

	public async getAdmissionStatus(offset?: number, range?: number): Promise<PaginatedResponse<ParticipationStatus>> {
		const response = await this.get<PaginatedResponse<ApiAdmissionStatus>>(`categories/admissionstatuses`, {
			offset,
			range,
		});
		const page: PaginatedResponse<ParticipationStatus> = {
			entities: transformApiAdmissionStatusesToAppParticipationStatuses(response.entities),
			offset: response.offset,
			range: response.range,
			totalEntities: response.totalEntities,
		};
		return page;
	}

	public async getSocialSecurityStatus(
		offset?: number,
		range?: number,
	): Promise<PaginatedResponse<SocialSecurityStatus>> {
		const response = await this.get<PaginatedResponse<ApiSocialSecurityStatus>>(`categories/socialsecuritystatuses`, {
			offset,
			range,
		});
		const page: PaginatedResponse<SocialSecurityStatus> = {
			entities: transformApiSocialSecurityStatusesToAppSocialSecurityStatuses(response.entities),
			offset: response.offset,
			range: response.range,
			totalEntities: response.totalEntities,
		};
		return page;
	}

	public async getSingle(
		clientId: number | string,
		careerId: number | string,
		assignmentId?: number | string,
	): Promise<ApiResponse<Career, CareerMetadata> | undefined> {
		const response = await this.get<ApiResponse<ApiCareer, ApiCareerMetadata>>(
			`clients/${clientId}/careers/${careerId}`,
			{
				assignmentId,
			},
		);
		if (!response.data) return;
		return {
			data: transformApiCareerToAppCareer(response.data),
			metadata: {
				...response.metadata,
			},
		};
	}
}

@Injectable()
export class CareerApiMock implements CareerApiProvider {
	public getAllByClient(
		_clientId: string | number,
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		offset?: number,
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		range?: number,
	): Promise<PaginatedResponse<Career>> {
		const page: PaginatedResponse<Career> = {
			entities: careerMocks,
			offset: 0,
			range: careerMocks.length,
			totalEntities: careerMocks.length,
		};
		return Promise.resolve(page);
	}
	public create(_clientId: string | number, _data: CreateCareer): Promise<void> {
		return Promise.resolve();
	}

	public update(_clientId: number | string, _data: UpdateCareer): Promise<void> {
		return Promise.resolve();
	}

	public removeEntry(_clientId: string | number, careerId: string | number): Promise<void> {
		const index = careerMocks.findIndex((c) => c.id === careerId);
		if (index !== -1) {
			careerMocks.splice(index, 1);
			return Promise.resolve();
		}
		return Promise.reject('Career not found');
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public getAdmissionStatus(offset?: number, range?: number): Promise<PaginatedResponse<ParticipationStatus>> {
		const page: PaginatedResponse<ParticipationStatus> = {
			entities: participationStatusMocks,
			offset: 0,
			range: participationStatusMocks.length,
			totalEntities: participationStatusMocks.length,
		};
		return Promise.resolve(page);
	}
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public getSocialSecurityStatus(offset?: number, range?: number): Promise<PaginatedResponse<SocialSecurityStatus>> {
		const page: PaginatedResponse<SocialSecurityStatus> = {
			entities: socialSecurityStatusMocks,
			offset: 0,
			range: socialSecurityStatusMocks.length,
			totalEntities: socialSecurityStatusMocks.length,
		};
		return Promise.resolve(page);
	}

	public getSingle(
		_clientId: number | string,
		careerId: number | string,
		_assignmentId?: number | string,
	): Promise<ApiResponse<Career, CareerMetadata> | undefined> {
		const career = careerMocks.find((c) => c.id === careerId);
		return Promise.resolve({
			data: career!,
			metadata: {
				editOnlyFields: [],
				allowedAdmissionStatusTransitionsIds: [],
			},
		});
	}
}

export const CAREER_API = new InjectionToken<CareerApiProvider>('CAREER_SERVICE');

export const provideCareerApi: Provider = {
	provide: CAREER_API,
	useClass: CareerApi,
};
